/**
 * MilestoneFeederSection — what a feeder is waiting on and what came back (docs/OPERATING_MODEL.md,
 * build step 3). Renders only on feeder milestones (a type set as a feeder in Templates → Milestone);
 * every other milestone renders nothing here.
 *
 * Sent / expected back (REQUIRED once sent, even as a guess, with "N days out" shown live) / came
 * back and what came back. On an Agency Review, recording "came back with comments" is the intake
 * (Matt, 2026-09-23): who the comments went to (same day, never held back), a required
 * game-changer answer (yes = the project's Slack channel + a red flag on the Tower tab), and the
 * next round's milestones, previewed and created only where the schedule doesn't have them yet.
 *
 * Section node in the MilestoneGateSection mould: lazy load into ctx.feederCache[msId]
 * (undefined = not started, null = in flight, false = not a feeder, {…} = loaded).
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { sectionKey } from '../outline/utils.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { alertDialog } from './Dialog.jsx'
import { getFeeder, setFeederDates, previewNextRound, recordFeederCameBack, daysFromToday, daysOutLabel, FEEDER_HANDOFF, expectedSourceLabel } from '../services/feeders.js'

const OWED = { agency: 'the City', consultant: 'a consultant', client: 'the client', gc: 'the GC' }
// What OUR half of the exchange is called, by who owes the feeder (Matt, 2026-09-23: "Not sent yet"
// didn't say what was being sent). Shared with the post window, which marks it sent on posting.
const handoffOf = (owedBy) => FEEDER_HANDOFF[owedBy] || { party: 'them', done: 'Sent', verb: 'sent', pending: 'NOT SENT YET', mark: 'Mark it sent…' }
const OUTCOME = { comments: 'with comments', approved: 'approved', other: 'something else' }
const RED = '#c0392b', GREEN = '#1D9E75'

function render() { window.dispatchEvent(new Event('outline-render')) }
function who() { const m = ctx.currentMember; return m?.fields?.['Member Name'] || m?.fields?.['Name'] || null }
function fmt(ymd) { return ymd ? new Date(ymd + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '' }
function todayYmd() { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) }

export default function MilestoneFeederSection({ msId, readOnly = false, sectionZoom = false }) {
  const key = sectionKey(msId, 'feeder')
  const isCollapsed = wf.collapsed[key] !== false
  const hasLoaded = useRef(false)
  const menu = useActionMenu()
  const [modal, setModal] = useState(null)   // 'dates' | 'intake' | null

  function maybeLoad() {
    if (hasLoaded.current) return
    hasLoaded.current = true
    if (!ctx.feederCache) ctx.feederCache = {}
    if (ctx.feederCache[msId] !== undefined) return
    ctx.feederCache[msId] = null
    getFeeder(msId)
      .then(r => { if (ctx.feederCache) ctx.feederCache[msId] = r.feeder ? r : false; render() })
      .catch(() => { if (ctx.feederCache) ctx.feederCache[msId] = false; render() })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { maybeLoad() }, [])

  const data = ctx.feederCache ? ctx.feederCache[msId] : undefined
  if (data === false) return null
  if (data === undefined || data === null) {
    if (sectionZoom) return <div className="wf-list"><div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>Loading…</div></div>
    return null
  }

  const { feeder, status: s, isAgencyReview } = data
  const owed = OWED[feeder.owedBy] || 'someone else'
  const H = handoffOf(feeder.owedBy)
  const late = s && s.asked_on && !s.arrived_on && s.expected_on && daysFromToday(s.expected_on) < 0
  const statusWord = s && s.arrived_on
    ? <span style={{ ...word, color: GREEN }}>CAME BACK {fmt(s.arrived_on).toUpperCase()} · {(OUTCOME[s.outcome] || '').toUpperCase()}</span>
    : s && s.asked_on
      ? <span style={{ ...word, color: late ? RED : 'var(--aj-text-mid)' }}>{late ? 'LATE' : 'WITH ' + owed.toUpperCase()} · EXPECTED {fmt(s.expected_on).toUpperCase()}</span>
      : <span style={{ ...word, color: 'var(--aj-text-muted)' }}>{H.pending}</span>

  function apply(next) { if (ctx.feederCache) ctx.feederCache[msId] = next.feeder ? next : false; render() }

  const rows = (
    <>
      <InfoRow k="sent" label={H.done} readOnly={readOnly}
        value={s && s.asked_on ? fmt(s.asked_on) : 'not yet'}
        onEdit={() => setModal('dates')} editLabel={s && s.asked_on ? 'Change dates…' : H.mark} />
      <InfoRow k="expected" label="Expected back" readOnly={readOnly}
        value={s && s.expected_on
          ? `${fmt(s.expected_on)} · ${daysOutLabel(s.expected_on)}${s.expected_source ? ' · ' + expectedSourceLabel(feeder.owedBy, s.expected_source) : ''}`
          : (s && s.asked_on ? 'required' : '—')}
        color={late ? RED : undefined} note={late ? 'Past the expected date: update it with the best guess you have.' : null}
        onEdit={() => setModal('dates')} editLabel="Change the expected date…" />
      <InfoRow k="back" label="Came back" readOnly={readOnly}
        value={s && s.arrived_on ? `${fmt(s.arrived_on)} · ${OUTCOME[s.outcome] || ''}` : 'not yet'}
        onEdit={() => setModal('intake')} editLabel={s && s.arrived_on ? 'Change what came back…' : 'Record that it came back…'} />
      {s && s.game_changer && (
        <InfoRow k="gc" label="Game changer" readOnly
          value={s.game_changer_note} color={s.game_changer_ack_at ? undefined : RED}
          note={s.game_changer_ack_at ? `Acknowledged by ${s.game_changer_ack_by || 'the tower'}` : 'Flagged on the Tower tab until Matt or Brandon acknowledges it.'} />
      )}
      {s && Array.isArray(s.sent_to) && s.sent_to.length > 0 && (
        <InfoRow k="to" label="Comments sent to" readOnly value={s.sent_to.map(x => x.name).join(', ')} />
      )}
      {s && s.no_consultant_items && <InfoRow k="to" label="Comments sent to" readOnly value="none were for consultants" />}
    </>
  )

  const modals = (
    <>
      {modal === 'dates' && <DatesModal msId={msId} data={data} onClose={() => setModal(null)} onSaved={(n) => { apply(n); setModal(null) }} />}
      {modal === 'intake' && <IntakeModal msId={msId} data={data} isAgencyReview={isAgencyReview} onClose={() => setModal(null)} onSaved={(n) => { apply(n); setModal(null) }} />}
    </>
  )

  if (sectionZoom) {
    return (
      <div className="wf-list">
        <div style={{ padding: '4px 0 10px', fontSize: 13, color: 'var(--aj-text-mid)' }}>
          Owed by {owed}{feeder.coordinator ? ` · ${feeder.coordinator} chases it` : ''}. {statusWord}
        </div>
        {rows}{modals}
      </div>
    )
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section" data-fn="zoomTo" data-level="section" data-sectionkey={key} title="Focus on the feeder"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Feeder</span>
          <span style={{ color: 'var(--aj-text-mid)', marginLeft: 8, fontSize: 13 }}>
            owed by {owed}{feeder.coordinator ? ` · ${feeder.coordinator} chases` : ''}
          </span>
          {statusWord}
        </div>
        {!readOnly ? <ActionMenuButton menu={menu} /> : <span />}
      </div>
      {!readOnly && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); setModal('dates') }}>{s && s.asked_on ? 'Change dates…' : H.mark}</ActionMenuItem>
          <ActionMenuItem onClick={() => { menu.close(); setModal('intake') }}>Record that it came back…</ActionMenuItem>
          <ActionMenuDivider />
          <ActionMenuItem onClick={() => { menu.close(); hasLoaded.current = false; if (ctx.feederCache) delete ctx.feederCache[msId]; maybeLoad() }}>Refresh</ActionMenuItem>
        </ActionMenu>
      )}
      {!isCollapsed && <div className="wf-children">{rows}</div>}
      {modals}
    </div>
  )
}

const word = { fontFamily: 'DM Mono, monospace', fontSize: 11, letterSpacing: '.06em', marginLeft: 10 }

function InfoRow({ k, label, value, color, note, readOnly, onEdit, editLabel }) {
  const menu = useActionMenu()
  return (
    <div className="wf-node wf-node--item" data-key={'feeder-row:' + k}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item"></span>
        <div className="wf-text-wrap" style={{ flexWrap: 'wrap', rowGap: 2 }}>
          <span className="wf-node-text" style={{ color: 'var(--aj-text-mid)' }}>{label}:</span>
          <span className="wf-node-text" style={{ marginLeft: 6, color: color || 'var(--aj-text)' }}>{value}</span>
          {note && <span style={{ fontSize: 11.5, color: color || 'var(--aj-text-muted)', marginLeft: 8 }}>{note}</span>}
        </div>
        {!readOnly && onEdit ? <ActionMenuButton menu={menu} /> : <span />}
      </div>
      {!readOnly && onEdit && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); onEdit() }}>{editLabel}</ActionMenuItem>
        </ActionMenu>
      )}
    </div>
  )
}

function Modal({ title, eyebrow, onClose, children, footer }) {
  return createPortal(
    <div style={M.backdrop} onClick={onClose}>
      <div style={M.modal} onClick={e => e.stopPropagation()} role="dialog" aria-label={title}>
        <div style={M.head}>
          <div><div style={M.eyebrow}>{eyebrow}</div><div style={M.title}>{title}</div></div>
          <button style={M.x} onClick={onClose} title="Close">×</button>
        </div>
        <div style={M.body}>{children}</div>
        <div style={M.foot}>{footer}</div>
      </div>
    </div>,
    document.body,
  )
}

function DatesModal({ msId, data, onClose, onSaved }) {
  const s = data.status || {}
  const H = handoffOf(data.feeder && data.feeder.owedBy)
  const [asked, setAsked] = useState(s.asked_on || data.suggestedAskedOn || todayYmd())
  const [expected, setExpected] = useState(s.expected_on || '')
  const [source, setSource] = useState(s.expected_source || 'estimate')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  async function save() {
    if (!expected) { setErr('An expected date is required, even as a guess. You will be prompted to update it if it passes.'); return }
    setBusy(true); setErr('')
    try { onSaved(await setFeederDates(msId, asked, expected, who(), source)) }
    catch (e) { setErr(String(e && e.message || e)) } finally { setBusy(false) }
  }
  return (
    <Modal eyebrow="Feeder" title={`When it was ${H.verb}, and when it's expected back`} onClose={onClose}
      footer={<><button style={M.btn} onClick={onClose}>Cancel</button><button style={M.btnPrimary} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button></>}>
      <label style={M.label}>{H.done} on</label>
      <input type="date" style={M.input} value={asked} onChange={e => setAsked(e.target.value)} />
      {data.suggestedAskedOn && !s.asked_on && <div style={M.hint}>Filled in from when the submission posted.</div>}
      <label style={{ ...M.label, marginTop: 14 }}>Expected back</label>
      <input type="date" style={M.input} value={expected} onChange={e => setExpected(e.target.value)} autoFocus />
      <div style={{ ...M.hint, color: expected ? 'var(--aj-text)' : 'var(--aj-text-muted)', fontFamily: 'DM Mono, monospace' }}>
        {expected ? daysOutLabel(expected) : 'Required, even as a guess.'}
      </div>
      <ExpectedSourcePick owedBy={data.feeder && data.feeder.owedBy} value={source} onChange={setSource} style={M} />
      {err && <div style={M.err}>{err}</div>}
    </Modal>
  )
}

/** Where the expected date came from: "The City gave us this date" / "Our estimate". The client
 *  copy attributes the date honestly from this (Matt, 2026-09-23). Exported for the post window. */
export function ExpectedSourcePick({ owedBy, value, onChange, style = M, name = 'expected-source' }) {
  const party = (FEEDER_HANDOFF[owedBy] || {}).party || 'they'
  const Party = party.charAt(0).toUpperCase() + party.slice(1)
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
      <label style={style.radio}><input type="radio" name={name} checked={value === 'them'} onChange={() => onChange('them')} /> {Party} gave us this date</label>
      <label style={style.radio}><input type="radio" name={name} checked={value === 'estimate'} onChange={() => onChange('estimate')} /> Our estimate</label>
    </div>
  )
}

function IntakeModal({ msId, data, isAgencyReview, onClose, onSaved }) {
  const s = data.status || {}
  const [arrived, setArrived] = useState(s.arrived_on || todayYmd())
  const [outcome, setOutcome] = useState(s.outcome || 'comments')
  const [sentTo, setSentTo] = useState((s.sent_to || []).map(x => x.name).join('\n'))
  const [noConsultants, setNoConsultants] = useState(!!s.no_consultant_items)
  const [gc, setGc] = useState(typeof s.game_changer === 'boolean' ? s.game_changer : null)
  const [gcNote, setGcNote] = useState(s.game_changer_note || '')
  const [plan, setPlan] = useState(undefined)
  const [createNext, setCreateNext] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState(null)
  const isIntake = isAgencyReview && outcome === 'comments'

  useEffect(() => {
    if (!isAgencyReview) return
    previewNextRound(msId).then(r => setPlan(r.plan)).catch(() => setPlan(null))
  }, [msId, isAgencyReview])

  async function save() {
    setErr('')
    const names = sentTo.split(/\n|,/).map(x => x.trim()).filter(Boolean)
    if (isIntake) {
      if (!names.length && !noConsultants) { setErr('Name who the comments went to, or tick "none were for consultants".'); return }
      if (gc === null) { setErr('Answer the game-changer question.'); return }
      if (gc && !gcNote.trim()) { setErr('Say what the game-changing comment is, in a sentence.'); return }
    }
    setBusy(true)
    try {
      const r = await recordFeederCameBack(msId, {
        arrivedOn: arrived, outcome, sentTo: names, noConsultantItems: noConsultants,
        gameChanger: isIntake ? gc : undefined, gameChangerNote: gcNote,
        createNextRound: isIntake && createNext && plan && plan.applies && plan.create.length > 0, memberName: who(),
      })
      if (r.nextRound && r.nextRound.created && r.nextRound.created.length) {
        const pid = ctx.recordId || wf.zoomProjectId
        if (pid && ctx.reloadProjectData) ctx.reloadProjectData(pid)
      }
      setDone(r)
    } catch (e) { setErr(String(e && e.message || e)) } finally { setBusy(false) }
  }

  if (done) {
    const nr = done.nextRound
    return (
      <Modal eyebrow="Feeder" title="Recorded" onClose={() => onSaved(done)}
        footer={<button style={M.btnPrimary} onClick={() => onSaved(done)}>Done</button>}>
        <div style={M.p}>It came back {OUTCOME[outcome]} on {fmt(arrived)}.</div>
        {done.status && done.status.game_changer && <div style={{ ...M.p, color: RED }}>The game changer was posted to the project's channel and flagged on the Tower tab.</div>}
        {nr && nr.created && nr.created.length > 0 && (
          <div style={M.p}>Added to the schedule: {nr.created.map(c => c.label).join('; ')}.
            {nr.rewired && nr.rewired.length > 0 && ` ${nr.rewired.join(', ')} now wait${nr.rewired.length === 1 ? 's' : ''} on the new review.`}</div>
        )}
        <div style={M.hint}>Next: post this milestone to close it.</div>
      </Modal>
    )
  }

  return (
    <Modal eyebrow="Feeder" title="It came back" onClose={onClose}
      footer={<><button style={M.btn} onClick={onClose}>Cancel</button><button style={M.btnPrimary} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Record it'}</button></>}>
      <label style={M.label}>Came back on</label>
      <input type="date" style={M.input} value={arrived} onChange={e => setArrived(e.target.value)} />
      <label style={{ ...M.label, marginTop: 14 }}>What came back</label>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {['comments', 'approved', 'other'].map(o => (
          <label key={o} style={M.radio}><input type="radio" checked={outcome === o} onChange={() => setOutcome(o)} /> {o === 'comments' ? 'Comments' : o === 'approved' ? 'Approved' : 'Something else'}</label>
        ))}
      </div>

      {isIntake && (
        <>
          <label style={{ ...M.label, marginTop: 16 }}>Who did the comments go to? (today, so nothing waits on us)</label>
          <textarea style={{ ...M.input, minHeight: 60, resize: 'vertical' }} value={sentTo} disabled={noConsultants}
            placeholder={'One consultant per line, e.g.\nStructural engineer\nCivil engineer'} onChange={e => setSentTo(e.target.value)} />
          <label style={M.radio}><input type="checkbox" checked={noConsultants} onChange={e => setNoConsultants(e.target.checked)} /> None were for consultants</label>

          <label style={{ ...M.label, marginTop: 16 }}>Any game-changing comments?</label>
          <div style={{ display: 'flex', gap: 14 }}>
            <label style={M.radio}><input type="radio" checked={gc === false} onChange={() => setGc(false)} /> No</label>
            <label style={M.radio}><input type="radio" checked={gc === true} onChange={() => setGc(true)} /> Yes</label>
          </div>
          {gc && (
            <>
              <textarea style={{ ...M.input, minHeight: 54, resize: 'vertical', marginTop: 6 }} value={gcNote} onChange={e => setGcNote(e.target.value)}
                placeholder="In a sentence: what is it, and why does it change things?" />
              <div style={{ ...M.hint, color: RED }}>This posts to the project's Slack channel and raises a red flag on the Tower tab.</div>
            </>
          )}

          <label style={{ ...M.label, marginTop: 16 }}>The next round</label>
          {plan === undefined && <div style={M.hint}>Checking the schedule…</div>}
          {plan === null && <div style={M.hint}>Could not check the schedule. Nothing will be added.</div>}
          {plan && !plan.applies && <div style={M.hint}>{plan.reason} Nothing will be added.</div>}
          {plan && plan.applies && plan.create.length === 0 && (
            <div style={M.p}>Already in the schedule: {[plan.existing.revision, plan.existing.resubmission, plan.existing.review].filter(Boolean).join(' → ')}. Nothing to add.</div>
          )}
          {plan && plan.applies && plan.create.length > 0 && (
            <>
              {(plan.existing.revision || plan.existing.resubmission) && (
                <div style={M.p}>Already in the schedule: {[plan.existing.revision, plan.existing.resubmission].filter(Boolean).join(' → ')}.</div>
              )}
              <label style={M.radio}><input type="checkbox" checked={createNext} onChange={e => setCreateNext(e.target.checked)} />
                Add: {plan.create.map(c => c.label + ' (' + c.subtitle + ')').join(' → ')}</label>
              {plan.rewire.length > 0 && <div style={M.hint}>{plan.rewire.map(r => r.name).join(', ')} will wait on the new review instead of this one.</div>}
            </>
          )}
        </>
      )}
      {err && <div style={M.err}>{err}</div>}
    </Modal>
  )
}

const M = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(7,39,45,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 800 },
  modal: { width: '100%', maxWidth: 560, maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, boxShadow: '0 16px 48px rgba(0,0,0,0.35)', overflow: 'hidden' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '14px 16px 8px' },
  eyebrow: { fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--aj-text-muted)' },
  title: { fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 15, color: 'var(--aj-text)', marginTop: 2 },
  x: { font: 'inherit', fontSize: 20, lineHeight: 1, background: 'transparent', border: 0, color: 'var(--aj-text-muted)', cursor: 'pointer', padding: '0 4px' },
  body: { padding: '6px 16px 12px', overflowY: 'auto', fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--aj-text)' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 16px 14px', borderTop: '0.5px solid var(--aj-border)' },
  label: { display: 'block', fontFamily: 'DM Mono, monospace', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--aj-text-muted)', marginBottom: 5 },
  input: { width: '100%', boxSizing: 'border-box', font: 'inherit', fontSize: 13, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)' },
  hint: { fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 5, lineHeight: 1.45 },
  p: { fontSize: 13, color: 'var(--aj-text)', margin: '4px 0', lineHeight: 1.5 },
  radio: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--aj-text)', marginTop: 4, cursor: 'pointer' },
  err: { marginTop: 10, fontSize: 12.5, color: RED },
  btn: { fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, padding: '6px 12px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' },
  btnPrimary: { fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, padding: '6px 12px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
}
